<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_REMITA_COLLECTIONS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(paymentDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(balanceDue)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>

      <template #cell(amountDeducted)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
      <template #cell(paymentStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="customerAccountHistoryRules">
        <b-form>
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="remita-customer-id"
                v-model="filter.remitaCustomerID"
                :label="$t('Remita Customer ID')"
                :placeholder="$t('Enter Remita Customer ID')"
                type="text"
              />
            </b-col>
            <!-- Loan Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.loanAccountNumber"
                :label="$t('Loan Account Number')"
                :placeholder="$t('Enter Loan Account Number')"
                type="text"
              />
            </b-col>
            <!-- Loan Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
              />
            </b-col>
            <!-- Loan Account Name -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.linkedNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="number"
              />
            </b-col>
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <b-col md="6">
              <InputField
                id="mandate-reference"
                v-model="filter.mandateReference"
                :label="$t('Mandate Reference')"
                :placeholder="$t('Enter Mandate Reference')"
                type="text"
              />
            </b-col>
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                for="ippis-oracle-id"
              />
            </b-col>
            <!--IPPIS/Oracle ID -->
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-from"
                v-model="filter.transactionStartDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (From)')"
                :error-name="$t('Transaction Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="transaction-date-to"
                v-model="filter.transactionEndDate"
                :min-date="new Date()"
                :label="$t('Transaction Date (To)')"
                :error-name="$t('Transaction Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->

            <!-- <b-col md="6">
              <SelectField
                id="payment-status"
                v-model="filter.paymentStatus"
                :reduce="option => option.value"
                label="Payment Status"
                placeholder="Select"
                :options="paymentStatusOptions"
                item="label"
              />
            </b-col> -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import Button from '../../../../components/button/Button.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import toNumber from '../../../../../helpers/toNumber'
import { toCustomDateTime, toDateWithYear, toDateWithYearFirst } from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import { reportPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    BBadge,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      query: '',
      getStatusColor,
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Remita Collections Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'remitaCustomerID', sortable: true, label: 'Remita Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'loanAccountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'linkedNumber', sortable: true, label: 'Linked Account Number' },
        { key: 'branchCode', sortable: true, label: 'Branch' },
        { key: 'productCode', sortable: true, label: 'Product Code' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'mandateReference', sortable: true, label: 'Mandate Reference' },
        { key: 'balanceDue', sortable: true, label: 'Balance Due' },
        { key: 'amountDeducted', sortable: true, label: 'Amount Deducted' },
        { key: 'paymentDate', sortable: true, label: 'Payment Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'requestID', sortable: true, label: 'Request ID' },
        { key: 'paymentStatus', sortable: true, label: 'Payment Status' },
        { key: 'statusReason', sortable: true, label: 'Status Reason' },
      ],
      productOptions: [],
      filter: {
        remitaCustomerID: '',
        loanAccountNumber: '',
        accountName: '',
        linkedNumber: '',
        productCode: '',
        branchCode: '',
        mandateReference: '',
        employeeID: '',
        transactionStartDate: null,
        transactionEndDate: null,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Remita Customer ID': item.remitaCustomerID,
        'Account Name': item.accountName,
        'Loan Account Number': item.loanAccountNumber,
        'Linked Account Number': item.linkedNumber,
        'Branch Code': item.branchCode,
        'Product Code': item.productCode,
        'Product Name': item.productName,
        'Mandate Reference': item.mandateReference,
        'Balance Due': item.balanceDue > 0 ? formatAmount(item.balanceDue) : 0,
        'Amount Deducted': item.amountDeducted > 0 ? formatAmount(item.amountDeducted) : 0,
        'Payment Date': item.paymentDate ? this.formatCustomDate(item.paymentDate) : '',
        'Transaction Date': item.transactionDate ? this.formatCustomDate(item.transactionDate) : '',
        'IPPIS/Oracle ID': item.employeeID,
        'Request ID': item.requestID,
        'Payment Status': item.paymentStatus,
        'Status Reason': item.statusReason,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchRemitaCollectionsReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchRemitaCollectionsReport()
    },
  },
  mounted() {
    this.fetchRemitaCollectionsReport()
    this.fetchProduct()
  },
  methods: {
    ...mapActions({
      getRemitaCollectionsReport: 'account/getRemitaCollectionsReport',
      getBranchByCode: 'operations/getBranchByCode',
      getProductByTypes: 'account/getProductByTypes',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchRemitaCollectionsReport()
    },
    async fetchProduct() {
      try {
        const response = await this.getProductByTypes({
          type1: 'Loan',
          type2: 'Savings',
          permission: reportPermissions.VIEW_REMITA_COLLECTIONS_REPORT,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.customerAccountHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchRemitaCollectionsReport()
        }
      })
    },
    async fetchRemitaCollectionsReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getRemitaCollectionsReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.VIEW_REMITA_COLLECTIONS_REPORT,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getRemitaCollectionsReport({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.EXPORT_REMITA_COLLECTIONS_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Remita Collections Report'],
            [`${(this.filter.transactionStartDate !== null && this.filter.transactionEndDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.transactionStartDate)} - ${this.formatCustomDate(this.filter.transactionEndDate)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Remita Collections Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getRemitaCollectionsReport({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.EXPORT_REMITA_COLLECTIONS_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Remita Collections Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Remita Collections Report'],
            [`${(this.filter.transactionStartDate !== null && this.filter.transactionEndDate !== null) ? `Date Range: ${this.formatCustomDate(this.filter.transactionStartDate)} - ${this.formatCustomDate(this.filter.transactionEndDate)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]

          const Data = this.allSearchedData.map(item => ({
            'Remita Customer ID': item.remitaCustomerID,
            'Account Name': item.accountName,
            'Loan Account Number': item.loanAccountNumber,
            'Linked Account Number': item.linkedNumber,
            'Branch Code': item.branchCode,
            'Product Code': item.productCode,
            'Product Name': item.productName,
            'Mandate Reference': item.mandateReference,
            'Balance Due': item.balanceDue > 0 ? toFormatAmount(item.balanceDue) : 0,
            'Amount Deducted': item.amountDeducted > 0 ? toFormatAmount(item.amountDeducted) : 0,
            'Payment Date': item.paymentDate ? this.formatCustomDate(item.paymentDate) : '',
            'Transaction Date': item.transactionDate ? this.formatCustomDate(item.transactionDate) : '',
            'IPPIS/Oracle ID': item.employeeID,
            'Request ID': item.requestID,
            'Payment Status': item.paymentStatus,
            'Status Reason': item.statusReason,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Remita Collections Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toDateWithYear,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
